// Precios por nivel en vivo: cuando cambia el carrito, vuelve a decidir qué nivel
// (al detal, mayorista, distribuidor o socio) muestra cada precio de la página.
// El estado inicial lo pinta el servidor (snippets/aq-precio-nivel.liquid); este
// script solo actúa después de un cambio de carrito.
//
// Qué niveles están activos lo decide el tema (snippets/aq-estado.liquid) y se pide a la
// sección aq-precios-estado: cada umbral se mide con el pedido a precio de su nivel, y
// /cart.js no trae esos precios.

const SECCION = 'aq-precios-estado';
const NOMBRE_BASE = 'Precio al detal';
const NOMBRES = {
  mayorista: 'Precio mayorista',
  distribuidor: 'Precio distribuidor',
  socio: 'Precio socio',
};

/**
 * Nivel de precio que aplica a un producto. Replica las reglas de la Discount
 * Function aq-precios-volumen y de snippets/aq-precio-nivel.liquid.
 *
 * @param {{esSocio: boolean, mayoristaActivo: boolean, distribuidorActivo: boolean}} estado
 * @param {{base: number, mayorista: number, distribuidor: number, socio: number}} precios - En centavos; 0 es faltante
 * @returns {'mayorista' | 'distribuidor' | 'socio' | null}
 */
export function nivelDe(estado, precios) {
  // Un socio nunca recibe mayorista. En empate gana el primer nivel de la lista.
  const niveles = estado.esSocio
    ? [['socio', true], ['distribuidor', estado.distribuidorActivo]]
    : [['mayorista', estado.mayoristaActivo], ['distribuidor', estado.distribuidorActivo]];

  let mejor = null;
  for (const [nivel, activo] of niveles) {
    const precio = precios[nivel];
    if (activo && precio > 0 && (mejor === null || precio < mejor.precio)) {
      mejor = { nivel, precio };
    }
  }

  return mejor !== null && mejor.precio < precios.base ? mejor.nivel : null;
}

/**
 * @param {Parameters<typeof nivelDe>[0]} estado
 */
function aplicar(estado) {
  for (const elemento of document.querySelectorAll('.aq-precio[data-b]')) {
    if (!(elemento instanceof HTMLElement)) continue;

    const datos = elemento.dataset;
    const nivel = nivelDe(estado, {
      base: Number(datos.b),
      mayorista: Number(datos.m),
      distribuidor: Number(datos.d),
      socio: Number(datos.s),
    });

    const nombre = elemento.querySelector('.aq-precio__etiqueta');
    if (nombre) nombre.textContent = nivel === null ? NOMBRE_BASE : NOMBRES[nivel];

    if (nivel === null) {
      elemento.removeAttribute('data-nivel');
      continue;
    }

    const valor = elemento.querySelector('.aq-precio__valor');
    if (valor) valor.textContent = datos[`${nivel[0]}t`] ?? '';
    elemento.dataset.nivel = nivel;
  }
}

/**
 * @param {string} raiz - Raíz de la tienda, con "/" al final
 * @returns {Promise<Parameters<typeof nivelDe>[0] | null>} null si los precios por nivel no aplican al visitante
 */
async function leerEstado(raiz) {
  const respuesta = await fetch(`${raiz}?section_id=${SECCION}`);
  if (!respuesta.ok) throw new Error(`HTTP ${respuesta.status}`);

  const documento = new DOMParser().parseFromString(await respuesta.text(), 'text/html');
  const json = documento.getElementById(SECCION)?.textContent;

  return json ? JSON.parse(json) : null;
}

function iniciar() {
  const raiz = window.Shopify?.routes?.root ?? '/';
  let ultima = 0;

  const actualizar = async () => {
    // Con varios cambios seguidos solo cuenta la respuesta del último.
    const turno = ++ultima;
    try {
      const estado = await leerEstado(raiz);
      if (estado && turno === ultima) aplicar(estado);
    } catch (error) {
      console.warn('[aq-precios] No se pudo leer el estado de precios:', error);
    }
  };

  document.addEventListener('shopify:cart:lines-update', (event) => {
    Promise.resolve(/** @type {any} */ (event).promise).then(actualizar, () => {});
  });

  // Al volver con el botón Atrás, la página restaurada puede traer un carrito viejo.
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) actualizar();
  });
}

if (typeof document !== 'undefined') {
  iniciar();
}
